'use client';

import { useEffect, useId, useMemo, useState, type ReactNode } from 'react';

import { Tooltip } from '../Tooltip';
import styles from './collapsible-container.module.scss';

export type CollapsibleContainerHeadingLevel = 1 | 2 | 3 | 4;

export type CollapsibleContainerProps = {
  id?: string;
  className?: string;
  headerClassName?: string;
  titleClassName?: string;
  contentClassName?: string;

  title: ReactNode;
  headingLevel?: CollapsibleContainerHeadingLevel;
  labelWhenCollapsed?: ReactNode;
  /** Optional tooltip text displayed next to the title */
  tooltip?: string;

  collapsed?: boolean;
  defaultCollapsed?: boolean;
  onToggle?: (nextCollapsed: boolean) => void;

  children: ReactNode;
};

export function CollapsibleContainer({
  id,
  className,
  headerClassName,
  titleClassName,
  contentClassName,
  title,
  headingLevel,
  labelWhenCollapsed,
  tooltip,
  collapsed,
  defaultCollapsed,
  onToggle,
  children
}: CollapsibleContainerProps) {
  const reactId = useId();
  const baseId = id ?? `collapsible-${reactId}`;
  const contentId = `${baseId}-content`;

  const isControlled = collapsed !== undefined;
  const [uncontrolledCollapsed, setUncontrolledCollapsed] = useState(
    defaultCollapsed ?? false
  );

  useEffect(() => {
    if (!isControlled) return;
    // Keep internal state aligned when switching modes.
    setUncontrolledCollapsed(collapsed);
  }, [collapsed, isControlled]);

  const isCollapsed = isControlled ? collapsed : uncontrolledCollapsed;

  const Heading = useMemo(() => {
    if (!headingLevel) return null;
    if (headingLevel === 1) return 'h1';
    if (headingLevel === 2) return 'h2';
    if (headingLevel === 3) return 'h3';
    return 'h4';
  }, [headingLevel]);

  const handleToggle = () => {
    const nextCollapsed = !isCollapsed;
    if (!isControlled) {
      setUncontrolledCollapsed(nextCollapsed);
    }
    onToggle?.(nextCollapsed);
  };

  return (
    <section
      id={id}
      className={[styles.container, className].filter(Boolean).join(' ')}
    >
      <button
        type="button"
        className={[styles.header, headerClassName].filter(Boolean).join(' ')}
        aria-expanded={!isCollapsed}
        aria-controls={contentId}
        onClick={handleToggle}
      >
        <span
          className={[styles.title, titleClassName].filter(Boolean).join(' ')}
        >
          {Heading ? (
            <Heading className={styles.heading}>{title}</Heading>
          ) : (
            title
          )}
          {tooltip ? <Tooltip content={tooltip} /> : null}
        </span>

        <span className={styles.toggle} aria-hidden="true">
          {isCollapsed && labelWhenCollapsed ? (
            <span className={styles.label}>{labelWhenCollapsed}</span>
          ) : null}
          <span
            className={[
              styles.chevron,
              isCollapsed ? styles.chevronCollapsed : ''
            ]
              .filter(Boolean)
              .join(' ')}
          />
        </span>
      </button>

      <div
        id={contentId}
        className={[
          styles.content,
          isCollapsed ? styles.contentCollapsed : '',
          contentClassName
        ]
          .filter(Boolean)
          .join(' ')}
        aria-hidden={isCollapsed}
      >
        {children}
      </div>
    </section>
  );
}
